/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AnyValidator } from '@squiz/dxp-console-support';
import { isFunction } from 'lodash';
import has from 'lodash/has';
import isString from 'lodash/isString';
import some from 'lodash/some';
import { useCallback, useState } from 'react';

/**
 * Form field id.
 */
export type FormFieldId = string;

/**
 * Form field value as a string or a function that returns a string.
 */
export type FormFieldValue = Function | string;

/**
 * Form error list.
 */
export type FormErrors = Record<FormFieldId, string | null>;

/**
 * Individual form field validation map.
 */
export interface FormFieldValidationMap {
  value: FormFieldValue;
  validators: Array<AnyValidator>;
}

/**
 * Form field list.
 */
export type FormFieldList = Record<FormFieldId, FormFieldValidationMap>;

/**
 * The result of using the form validation hook.
 */
export interface FormValidationResult {
  clearErrors(): void;
  errors: FormErrors;
  validate(value: string, validationMap: Array<AnyValidator>): FormFieldValidationResponse;
  validateList(list: FormFieldList): FormFieldValidationResponse;
}

/**
 * Field validation response.
 * @export
 */
export interface FormFieldValidationResponse {
  /**
   * Returns true if the validation response is valid.
   */
  isValid: boolean;

  /**
   * Validation messages object (contains the top most error).
   */
  messages: FormErrors;
}

/**
 * Form validation hook.
 * @returns {FormValidationResult} The result of the hook.
 */
export const useFormValidation = (): FormValidationResult => {
  const [errors, setErrors] = useState<FormErrors>({});

  /**
   * Does field validation on a given value and set of validators.
   * @param value The value to check.
   * @param validationMap An array of validators to use.
   * @returns Validation responses including error messages and validation state.
   */
  const validate = useCallback((value: string, validationMap: Array<AnyValidator>): FormFieldValidationResponse => {
    const messages = validationMap
      .map((v) => v.validate(value) as FormErrors)
      .reduce((prev, curr) => {
        const entries = Object.entries(curr);
        const [key, message] = entries[0];
        if (!has(prev, key) || (isString(message) && !isString(prev[key]))) {
          prev[key] = message;
        }
        return prev;
      }, {});
    return {
      isValid: !some(Object.values(messages)),
      messages,
    };
  }, []);

  /**
   * Triggers validation of a list of values
   * Note: uses actual input values rather than event state.
   */
  const validateList = useCallback(
    (list: FormFieldList): FormFieldValidationResponse => {
      let nextErrors: FormErrors = { ...errors };
      Object.entries(list).forEach(([id, field]) => {
        const value = isFunction(field.value) ? field.value() : (field.value as string);
        const { messages } = validate(value, field.validators);
        nextErrors = {
          ...nextErrors,
          ...(messages as FormErrors),
        };
        setErrors(nextErrors);
      });
      return {
        isValid: !some(Object.values(nextErrors)),
        messages: nextErrors,
      };
    },
    [errors, validate],
  );

  return {
    clearErrors: (): void => setErrors({}),
    errors,
    validate,
    validateList,
  };
};
